import type { AABB, OBB, Vec2 } from '../../geo/types';
import { obbCorners, ringBounds } from '../../geo/polygon';
import { boxSpans, spansOverlapSegment } from '../../geo/sat';
import type { ReservedFootprint } from './types';
import { obbToAABB } from './lotObb';
import { createCellGrid } from './cellGrid';
import { createCellTable, type CellTable } from './cellTable';
import { bandRing, inBandedRing, type BandedRing } from './bandedRing';

/* A wall's outline runs to hundreds of vertices; a box asks only the edges filed in its cells. */
const EDGE_CELL = 10;
/* The box's bounds grow by far more than rounding and far less than any edge, so no edge the box touches is skipped. */
const PAD = 1e-6;

/** A reserved footprint's outline, its edges filed by cell and by height band. */
interface Reserved {
  readonly ring: readonly Vec2[];
  readonly bounds: AABB;
  readonly banded: BandedRing;
  readonly edges: CellTable;
}

export interface ReservedIndex {
  /** Whether a box overlaps the outline of a reserved footprint; bounds alone never refuse it. */
  overlaps(obb: OBB): boolean;
}

function boxesMeet(a: AABB, b: AABB): boolean {
  return a.maxX >= b.minX && a.minX <= b.maxX && a.maxY >= b.minY && a.minY <= b.maxY;
}

function reservedOf(footprint: ReservedFootprint): Reserved {
  const ring = footprint.polygon.outer;
  const edgeBounds = ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    return {
      minX: Math.min(a[0], b[0]),
      minY: Math.min(a[1], b[1]),
      maxX: Math.max(a[0], b[0]),
      maxY: Math.max(a[1], b[1]),
    };
  });
  return {
    ring,
    bounds: ringBounds(ring),
    banded: bandRing(ring),
    edges: createCellTable(edgeBounds, EDGE_CELL),
  };
}

function insideBox(p: Vec2, obb: OBB): boolean {
  const dx = p[0] - obb.center[0];
  const dy = p[1] - obb.center[1];
  return (
    Math.abs(dx * obb.axisU[0] + dy * obb.axisU[1]) <= obb.halfU &&
    Math.abs(dx * -obb.axisU[1] + dy * obb.axisU[0]) <= obb.halfV
  );
}

/**
 * obbPolygonOverlap's answer: a corner of the box inside the outline, a vertex of the outline inside
 * the box, or an edge of it across the box. A vertex or an edge that meets the box lies in a cell
 * the box's bounds cross, so only those cells' edges and their first vertices are asked.
 */
function overlapsOutline(obb: OBB, box: AABB, reserved: Reserved): boolean {
  const { ring, banded, edges } = reserved;
  if (ring.length < 3) return false;
  if (obbCorners(obb).some((corner) => inBandedRing(corner, banded))) return true;
  const spans = boxSpans(obb);
  const near = {
    minX: box.minX - PAD,
    minY: box.minY - PAD,
    maxX: box.maxX + PAD,
    maxY: box.maxY + PAD,
  };
  return edges.some(near, (i) => {
    const a = ring[i]!;
    return insideBox(a, obb) || spansOverlapSegment(obb, spans, a, ring[(i + 1) % ring.length]!);
  });
}

/*
 * A wall's reservation runs the length of a district, so its bounds cover whole blocks: bounds
 * only pick the footprints worth testing, and the outline decides.
 */
export function createReservedIndex(reserved: readonly ReservedFootprint[]): ReservedIndex {
  const grid = createCellGrid<Reserved>(20);
  for (const footprint of reserved) {
    const filed = reservedOf(footprint);
    grid.insert(filed, filed.bounds);
  }
  return {
    overlaps(obb) {
      const box = obbToAABB(obb);
      return grid.some(
        box,
        (filed) => boxesMeet(box, filed.bounds) && overlapsOutline(obb, box, filed)
      );
    },
  };
}
